import { Typography, Box, Theme } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/lab/PaginationItem';
import { useRouter } from 'next/router';
import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResultsSuccess } from '@/__generated__/getSearchResults';

interface PaginationProp {
  pagination: SEOSegmentationSearchResultsSuccess['pagination'];
  onPageClick: Function;
  segmentId: string;
  pageSelected: number;
}

const OffsetPagination = ({ pagination, onPageClick, segmentId, pageSelected }: PaginationProp) => {
  const hasPages = pagination.numberOfPages > 1;
  const hasResults = pagination.numberOfPages > 0;
  const isFirstPage = pageSelected === 1;
  const isLastPage = pageSelected === pagination.numberOfPages;
  const router = useRouter();

  const handleChangePage = (event: any, newPage: number) => {
    event.preventDefault();
    onPageClick(newPage);

    // only show "page" for pages > 1; "/senior-care-page1" should just be "/senior-care"
    const url = newPage === 1 ? segmentId : `${segmentId}-page${newPage.toString()}`;
    router.push(url, undefined, { shallow: true });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      borderTop={(theme: Theme) => (hasResults ? `1px solid ${theme.palette.care.grey[300]}` : '')}
      borderBottom={(theme: Theme) =>
        hasResults ? `1px solid ${theme.palette.care.grey[300]}` : ''
      }
      mx={{ xs: -2, sm: 0 }}>
      {hasPages && (
        <Box mt={3} mb={2.25}>
          <Pagination
            count={pagination.numberOfPages}
            onChange={handleChangePage}
            page={pageSelected}
            renderItem={(item) => {
              const isNextButton = item.type === 'next';
              const isPrevButton = item.type === 'previous';

              return (
                <PaginationItem
                  component={
                    (isNextButton && isLastPage) || (isPrevButton && isFirstPage) ? 'div' : 'a'
                  }
                  href={
                    (isNextButton && isLastPage) || (isPrevButton && isFirstPage)
                      ? undefined
                      : `${segmentId}${
                          item.page === 1 || item.page === 0 ? '' : `-page${item.page}`
                        }`
                  }
                  role="button"
                  {...item}
                />
              );
            }}
            sx={{
              '& .MuiPaginationItem-page': {
                minWidth: '24px',
                height: '24px',
              },
            }}
          />
        </Box>
      )}
      {hasResults && (
        <Typography role="heading" variant="body3" color="primary" mb={3}>
          Showing {pagination.startIndex + 1} - {pagination.endIndex} of{' '}
          {pagination.totalNumberOfResults}
        </Typography>
      )}
    </Box>
  );
};

export default OffsetPagination;
